import React from 'react'
import { createRoot } from 'react-dom/client'
import '@fontsource/poppins/400.css'
import '@fontsource/cairo/400.css'
import App from './App.tsx'
import './index.css'
import { initSentry, SentryErrorBoundary } from './lib/sentry'

// Initialize Sentry error monitoring. No-op when VITE_SENTRY_DSN is not set.
// Must run before React renders so it can catch errors during initial mount.
initSentry();

/**
 * Top-level error boundary. Catches anything React's render tree throws
 * that wasn't caught by a deeper boundary. Reports to Sentry (no-op
 * without DSN) and shows a friendly "reload" UI.
 */
function ErrorFallback() {
  const isMobile = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isArabic = typeof window !== 'undefined' && window.location.pathname.startsWith('/ar-eg');
  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: isMobile ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' : 'system-ui, sans-serif',
        padding: '1rem',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: isMobile ? 300 : 400 }}>
        <h2 style={{ marginBottom: '1rem', fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
          {isArabic ? 'حصل خطأ ما' : 'Something went wrong'}
        </h2>
        <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.875rem' }}>
          {isArabic
            ? 'حاول تعمل تحديث للصفحة. لو المشكلة استمرت، اتصل بنا.'
            : 'Try reloading the page. If the problem persists, contact support.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: isMobile ? '0.875rem' : '1rem',
          }}
        >
          {isArabic ? 'تحديث الصفحة' : 'Reload Page'}
        </button>
      </div>
    </div>
  );
}

// Register service worker only for vendor pages with mobile optimizations
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const isVendorPage = window.location.pathname.startsWith('/vendor');
    
    // Only register service worker for vendor pages
    if (isVendorPage) {
      // Add mobile-specific optimizations
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // For mobile devices, use a more aggressive caching strategy
      const swOptions = {
        scope: '/vendor/',
        updateViaCache: (isMobile ? 'none' : 'all') as ServiceWorkerUpdateViaCache // Disable cache for mobile to ensure fresh content
      };

      navigator.serviceWorker.register('/vendor-sw.js', swOptions)
        .then((registration) => {
          // Service worker registered successfully
          console.log('Vendor service worker registered:', registration);
          
          // For mobile devices, check for updates more frequently
          if (isMobile) {
            registration.update();
          }
        })
        .catch((registrationError) => {
          // Service worker registration failed
          console.error('Vendor service worker registration failed:', registrationError);
        });
    }
  });
}

function setBodyDirection() {
  const isArabic = window.location.pathname.startsWith('/ar-eg/');
  document.body.dir = isArabic ? 'rtl' : 'ltr';
}

function loadNonCriticalFonts() {
  const loadDeferredFonts = () => {
    void Promise.all([
      import('@fontsource/poppins/600.css'),
      import('@fontsource/poppins/700.css'),
      import('@fontsource/cairo/700.css'),
    ]);
  };

  const winWithIdle = window as Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  };

  if (typeof winWithIdle.requestIdleCallback === 'function') {
    winWithIdle.requestIdleCallback(loadDeferredFonts, { timeout: 2000 });
    return;
  }

  window.setTimeout(loadDeferredFonts, 1200);
}

// Keep startup lightweight to reduce layout shifts on first paint.
function preventFOUC() {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Remove FOUC fix class after a short delay.
  setTimeout(() => {
    document.body.classList.add('loaded');
    const foucElements = document.querySelectorAll('.fouc-fix');
    foucElements.forEach(el => el.classList.add('loaded'));
  }, isMobile ? 50 : 100);
}

// Initialize app with mobile optimizations
function initializeApp() {
  try {
    setBodyDirection();
    preventFOUC();
    loadNonCriticalFonts();
    
    const rootElement = document.getElementById('root');
    if (rootElement) {
      const root = createRoot(rootElement);
      root.render(
        <React.StrictMode>
          <SentryErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <App />
          </SentryErrorBoundary>
        </React.StrictMode>
      );
    }
  } catch (error) {
    console.error('Failed to initialize app:', error);
    // Show fallback error message with mobile-friendly styling
    const rootElement = document.getElementById('root');
    if (rootElement) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      rootElement.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: ${isMobile ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' : 'system-ui, sans-serif'}; padding: 1rem;">
          <div style="text-align: center; max-width: ${isMobile ? '300px' : '400px'};">
            <h2 style="margin-bottom: 1rem; font-size: ${isMobile ? '1.25rem' : '1.5rem'};">Something went wrong</h2>
            <button onclick="window.location.reload()" style="padding: 0.75rem 1.5rem; background: #2563eb; color: white; border: none; border-radius: 0.375rem; cursor: pointer; font-size: ${isMobile ? '0.875rem' : '1rem'};">
              Reload Page
            </button>
          </div>
        </div>
      `;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}